import React, { useCallback, useContext, useEffect, useState } from 'react';
import {
	fetchWheelChartData,
	getPreviewDirectCompanyDetails,
	getPreviewIndirectCompanyDetails,
} from 'utils/apiRequests';
import { QueryStringContext } from 'contexts/QueryStringContext';
import { SettingsOptionsContext } from 'contexts/SettingsOptionsContext';
import { getCurrencySymbolFromOptions } from 'Details/utils';
import { taxonomyMapperDataConverter } from 'utils/taxonomyMapperDataConverter';
import { TAXONOMY_TREE_DATA_PARAMS } from 'TaxonomyMapper/TaxonomyMapperContainer';
import {
	getAllParentsInfo,
	mapOverTree,
	searchTree,
} from 'TaxonomyMapper/utils';
import {
	Content,
	Wrapper,
	ResizeContainer,
	ResizeHandleIcon,
} from './TaxonomyPreview.styles';
import DirectHeadlines from './DirectHeadlines';
import IndirectHeadlines from './IndirectHeadlines';
import SecondaryNav from './SecondaryNav';
import TaxonomyGrid from './TaxonomyGrid';
import TaxonomyTree from './TaxonomyTree';

export const AP_TAXONOMY_TREE_DATA_PARAMS = {
	dimensions: ['apCategory', 'apCategory', 'apCategory', 'apCategory'],
	harmonized: true,
	isPmi: true,
	filters: 'spendType',
	filterValues: 1,
	disableTailSpendSegments: true,
};

export default ({ apTaxonomy = false }) => {
	const queryStringState = useContext(QueryStringContext);
	const { options } = useContext(SettingsOptionsContext);
	const [companies, setCompanies] = useState([]);
	const [treeData, setTreeData] = useState([]);
	const [companyTreeData, setCompanyTreeData] = useState([]);
	const [searchQuery, setSearchQuery] = useState('');

	const [highlightItem, setHighlightItem] = useState('');
	const [gridHeight, setGridHeight] = useState(440);
	const { currency: currencyList } = options;

	const {
		company,
		currency,
		selectedTreeItem,
		setQueryStringStateAndPush,
	} = queryStringState;
	const currencySymbol = getCurrencySymbolFromOptions(currencyList, currency);
	useEffect(() => {
		const detailsMethod = apTaxonomy
			? getPreviewIndirectCompanyDetails
			: getPreviewDirectCompanyDetails;

		detailsMethod({ currency, currencyType: 'fixed' }).then(result => {
			setCompanies(result ? result.filter(company => company.spend) : result);
			if (!apTaxonomy && !company) {
				setQueryStringStateAndPush({
					company: result[0].company,
				});
			}
		});

		const apiRequestParams = {
			...(apTaxonomy
				? AP_TAXONOMY_TREE_DATA_PARAMS
				: TAXONOMY_TREE_DATA_PARAMS),
		};

		if (currency) {
			apiRequestParams.currency = currency;
			apiRequestParams.currencyType = 'fixed';
		}
		fetchWheelChartData(apiRequestParams).then(data => {
			const treeData = taxonomyMapperDataConverter
				.convert(data.data, apTaxonomy)
				.map(data => ({
					...data,
					expanded: true,
				}));
			setTreeData(treeData);
			let companyTreeData = [];
			if (apTaxonomy) {
				const topLevelItem = {
					name: 'AlixPartners taxonomy',
					id: 'AlixPartners taxonomy',
					originalId: 'AlixPartners taxonomy',
					level: -1,
					children: [...treeData],
					expanded: true,
					value: treeData.reduce((acc, item) => acc + item.value, 0),
				};

				let apTreeData = [topLevelItem];

				apTreeData = mapOverTree(apTreeData, item => {
					const level = item.level + 1;

					return {
						...item,
						level,
						expanded: false,
					};
				});
				apTreeData[0].expanded = true;
				companyTreeData = apTreeData;
			} else if (company) {
				companyTreeData = treeData.filter(item => item.name === company);
			}
			if (companyTreeData.length) {
				setCompanyTreeData(companyTreeData);
			}
			if (selectedTreeItem && companyTreeData) {
				const categoryItem = searchTree(companyTreeData[0], selectedTreeItem);
				if (categoryItem) {
					setTimeout(() => {
						setHighlightItem(selectedTreeItem);
					}, 1000);
				}
			}
		});
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);
	useEffect(
		() => {
			if (company) {
				setCompanyTreeData(treeData.filter(item => item.name === company));
			}
		},
		[company, treeData]
	);
	const clearSearchQuery = useCallback(() => {
		setSearchQuery('');
		setHighlightItem('');
	}, []);

	useEffect(
		() => {
			document.addEventListener('click', clearSearchQuery, true);
			return () => {
				document.removeEventListener('click', clearSearchQuery, true);
			};
		},
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[]
	);
	const selectedCompany = companies.find(c => c.company === company);
	let startY, startHeight;
	const maxGridHeight = window.innerHeight - 88;
	const doDrag = e => {
		const height = startHeight + e.clientY - startY;
		setGridHeight(Math.max(Math.min(height, maxGridHeight), 200));
	};

	const stopDrag = e => {
		document.documentElement.removeEventListener('mousemove', doDrag, false);
		document.documentElement.removeEventListener('mouseup', stopDrag, false);
	};

	let categoryProp = null;
	if (selectedTreeItem && companyTreeData && companyTreeData[0]) {
		const items = getAllParentsInfo(companyTreeData[0], selectedTreeItem);

		if (items.length) {
			categoryProp = items.reduce((acc, item) => {
				const key = apTaxonomy
					? `AP_L${item.level}Category`
					: `clientCategoryL${item.level}`;
				return {
					...acc,
					[key]: item.name,
				};
			}, {});
		}
	}
	return (
		<Wrapper>
			<SecondaryNav
				apTaxonomy={apTaxonomy}
				companies={companies.map(c => c.company)}
				selectedCompany={selectedCompany ? selectedCompany.company : null}
				setSelectedCompany={company => {
					setQueryStringStateAndPush({ company });
				}}
			/>
			<Content height={gridHeight}>
				{apTaxonomy ? (
					<IndirectHeadlines
						companies={companies}
						currencySymbol={currencySymbol}
					/>
				) : (
					<DirectHeadlines
						{...selectedCompany}
						currency={currency}
						currencySymbol={currencySymbol}
					/>
				)}
				<TaxonomyTree
					company={selectedCompany}
					companyTreeData={companyTreeData}
					setCompanyTreeData={setCompanyTreeData}
					currency={currency}
					currencySymbol={currencySymbol}
					onSelectTreeItem={treeItemId => {
						setQueryStringStateAndPush({ selectedTreeItem: treeItemId });
					}}
					searchQuery={searchQuery || highlightItem}
					setSearchQuery={setSearchQuery}
				/>
			</Content>
			<ResizeContainer>
				<ResizeHandleIcon
					onMouseDown={e => {
						startY = e.clientY;
						startHeight = gridHeight;
						document.documentElement.addEventListener(
							'mousemove',
							doDrag,
							false
						);
						document.documentElement.addEventListener(
							'mouseup',
							stopDrag,
							false
						);
					}}
					iconName="baseline_drag_handle"
				/>
			</ResizeContainer>
			<TaxonomyGrid
				apTaxonomy={apTaxonomy}
				company={apTaxonomy ? null : company}
				categoryFilter={categoryProp}
			/>
		</Wrapper>
	);
};
